
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Eye, EyeOff, ArrowLeft, Briefcase } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

const StaffLogin = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { signIn } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await signIn(email, password);
      if (error) {
        toast({
          title: "Login Failed",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Welcome back!",
          description: "Redirecting to staff dashboard...",
        });
        navigate('/app/staff/dashboard');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-blue-50 to-slate-50">
      <Card className="w-full max-w-md bg-white/95 backdrop-blur-sm shadow-lg border border-blue-200/30">
        <CardHeader className="text-center space-y-4">
          <div className="flex items-center justify-center mb-4">
            <Link 
              to="/login"
              className="absolute left-6 p-2 rounded-lg hover:bg-blue-50/50 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </Link>
            <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center bg-gradient-to-br from-blue-600 to-slate-700">
              <Briefcase className="text-white w-8 h-8" />
            </div>
          </div>
          <div>
            <CardTitle className="text-2xl font-bold text-slate-800">
              Staff Access Only
            </CardTitle>
            <p className="text-slate-600 mt-2">
              Log in to manage member check-ins, track patient credits, and access training tools.
            </p>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
              <div className="relative">
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="border-slate-300 bg-white focus:border-blue-500 focus:ring-blue-500/20 pl-10"
                  required
                />
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-500" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="border-slate-300 bg-white focus:border-blue-500 focus:ring-blue-500/20 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:opacity-70 text-slate-500"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="w-full text-white hover:opacity-90 transition-all duration-300 bg-gradient-to-r from-blue-600 to-slate-700"
            >
              {loading ? 'Signing in...' : 'Log In as Staff'}
            </Button>
          </form>

          <div className="text-center text-sm text-slate-600">
            Need help? 
            <span className="ml-1 font-medium text-blue-600">
              Contact your manager or HQ Support
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffLogin;
